import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  HipaaESignRequestModal,
  useHipaaESignStatus,
} from '@/components/hipaa-esign';
import { MatterClientModalWrapper } from '@/components/matter-notes-v2/matter-client-v2';
import { SignatureUploadModal } from '@/components/matter-notes-v2/matter-client-v2/components/SignatureUploadModal';
import { Dialog, DialogTitle } from '@/components/ui/catalyst/dialog';
import { Text } from '@/components/ui/catalyst/text';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import { useHipaaAuthorizationCreateOrUpdateMutation } from '@/services/api/hipaaAuthorizationService';

import { useRequestPage } from './contexts/RequestPageContext';
import { RequestMethodModal } from './modals/RequestMethodModal';
import { ManualUploadPanel } from './package-edit/ManualUploadPanel';
import { MissingDataBanner } from './package-edit/MissingDataBanner';
import { PackageComponentsList } from './package-edit/PackageComponentsList';
import { MemoizedIframePreview } from './package-view/MemoizedIframePreview';
import { PackagePreviewPane } from './package-view/PackagePreviewPane';

// ============================================================================
// Main HIPAA Package Tab Component
// ============================================================================

/**
 * Unified HIPAA Package Tab - handles both edit and view modes
 *
 * This component manages the complete HIPAA package workflow:
 * - Edit mode: Component list (editable) + preview pane
 * - View mode: Component list (read-only) + preview pane
 * - Missing data banner for validation issues
 * - Modal orchestration for uploads, signatures, and client info
 */
export const HipaaPackageTab = () => {
  const {
    request,
    provider,
    matter,
    matterId,
    requestId,
    primaryClient,
    packageData,
    isLoadingPackage,
    refetchPackage,
    modals,
    closeModal,
  } = useRequestPage();

  // Mutation for signature upload
  const [createOrUpdateSignature, { isLoading: isUploadingSignature }] =
    useHipaaAuthorizationCreateOrUpdateMutation();

  // E-sign status hook (with refresh on mount if pending)
  const { sendRequest: sendESignRequest, isSending: isSendingESign } =
    useHipaaESignStatus({ matterId, refreshOnMount: true });

  // Client contact permission
  const canContactClient = matter?.contact_client_directly !== false;
  // Loading state
  if (isLoadingPackage || !packageData) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 mx-auto mb-3" />
          <Text className="text-sm text-gray-600">Loading package...</Text>
        </div>
      </div>
    );
  }

  // Safety check
  if (!request || !provider) return null;

  // ======== Event Handlers ========

  const handleClientInfoComplete = () => {
    closeModal('clientInfo');
    refetchPackage();
  };

  const handleSignatureUpload = async (file: File, signatureDate: string) => {
    if (!primaryClient?.id) {
      toast.error('Client ID not found');
      return;
    }

    try {
      await createOrUpdateSignature({
        clientId: primaryClient.id,
        body: {
          file,
          signature_date: signatureDate,
        },
      }).unwrap();

      closeModal('signature');
      toast.success('HIPAA signature uploaded successfully');
      refetchPackage();
    } catch {
      toast.error('Failed to upload HIPAA signature');
    }
  };

  const handleRequestMethodComplete = () => {
    closeModal('requestMethod');
    refetchPackage();
  };

  const handleUploadComplete = () => {
    closeModal('upload');
    refetchPackage();
  };

  const clientWarningMessages: string[] = [
    'Packages without a client ID are less persuasive to providers when approving authorizations.',
  ];
  if (!primaryClient?.id) {
    clientWarningMessages.push(
      'We could not find a primary client for this matter. Add client details before sending this package.',
    );
  }

  // ======== Render ========

  return (
    <div className="flex h-full flex-col bg-[#f6f7fb]">
      <div className="flex items-start gap-2 border-b border-zinc-200 bg-[#f3f4f7] px-6 py-4 text-zinc-700">
        <div className="space-y-0.5 text-sm leading-snug">
          <p className="font-semibold text-zinc-950">
            Client identification suggested
          </p>
          {clientWarningMessages.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      </div>

      {/* Missing Data Banner */}
      <MissingDataBanner />

      {/* Main Content Area - Unified Layout */}
      <div className="flex-1 overflow-hidden flex">
        <UnifiedPackageLayout
          onComponentsChange={refetchPackage}
        />
      </div>

      {/* ======== Modals ======== */}

      {/* Manual Upload Modal */}
      <Dialog
        open={modals.upload}
        onClose={() => closeModal('upload')}
        size="3xl"
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      >
        <DialogTitle>Upload Complete HIPAA Package</DialogTitle>
        <ManualUploadPanel
          onComplete={handleUploadComplete}
          onCancel={() => closeModal('upload')}
        />
      </Dialog>

      {/* Client Info Modal */}
      {modals.clientInfo && primaryClient && (
        <MatterClientModalWrapper
          isOpen={modals.clientInfo}
          onClose={handleClientInfoComplete}
          client={primaryClient}
          matterId={matterId}
          includeDateOfLoss
          zIndex={Z_INDEX_MODAL_NESTED}
          nested
        />
      )}

      {/* Signature Upload Modal */}
      {modals.signature && primaryClient && (
        <SignatureUploadModal
          isOpen={modals.signature}
          onClose={() => closeModal('signature')}
          onUpload={handleSignatureUpload}
          isUploading={isUploadingSignature}
          zIndex={Z_INDEX_MODAL_NESTED}
        />
      )}

      {/* Request Method Modal */}
      {modals.requestMethod && provider && (
        <RequestMethodModal
          onClose={handleRequestMethodComplete}
          matterId={matterId}
          providerId={request.provider_id}
          requestId={requestId}
          request={request}
          provider={provider}
          zIndex={Z_INDEX_MODAL_NESTED}
          nested
        />
      )}

      {/* E-Sign Request Modal */}
      {modals.eSign && primaryClient && (
        <HipaaESignRequestModal
          isOpen={modals.eSign}
          onClose={() => closeModal('eSign')}
          onSubmit={async (params) => {
            await sendESignRequest(params);
            refetchPackage();
          }}
          isSubmitting={isSendingESign}
          clientName={
            primaryClient.name?.trim() ||
            `${primaryClient.first_name || ''} ${primaryClient.last_name || ''}`.trim()
          }
          clientEmail={primaryClient.email}
          clientPhone={primaryClient.phone_numbers?.[0]?.phone_number}
          firmName={matter?.firm?.name}
          canContactClient={canContactClient}
          zIndex={Z_INDEX_MODAL_NESTED}
        />
      )}
    </div>
  );
};

// ============================================================================
// Unified Package Layout (Both View and Edit Modes)
// ============================================================================

interface UnifiedPackageLayoutProps {
  onComponentsChange: () => void;
}

/**
 * Unified layout for both view and edit modes
 * - Left: Component list (editable in edit mode, read-only in view mode)
 * - Center: Preview pane (component-level in edit, full package in view)
 */
const UnifiedPackageLayout = ({
  onComponentsChange,
}: UnifiedPackageLayoutProps) => {
  const { packageData, matterId, packageMode } = useRequestPage();

  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(
    null,
  );
  const [showAddPanel, setShowAddPanel] = useState(false);
  const components = useMemo(() => packageData?.components || [], [packageData]);
  const isViewMode = packageMode === 'view';
  const isGenerating = packageData?.status === 'generating';
  const isManualUpload = packageData?.package_source === 'manually_uploaded';

  useEffect(() => {
    if (isViewMode) return;

    if (components.length === 0) {
      if (selectedComponentId !== null) {
        setSelectedComponentId(null);
      }
      return;
    }

    const selectedStillExists = components.some(
      (component) => component.id === selectedComponentId,
    );
    if (!selectedStillExists) {
      setSelectedComponentId(components[0].id);
    }
  }, [components, isViewMode, selectedComponentId]);

  if (!packageData) return null;

  // In view mode, show the rendered package URL
  const urlToRender = isManualUpload
    ? packageData.download_url
    : packageData.download_url || packageData.rendered_pdf_url;

  return (
    <div className="flex-1 overflow-hidden bg-[#f6f7fb]">
      <div className="flex h-full">
        {/* Component List - Anchored Left (Both Modes) */}
        <div className="w-[320px] flex-shrink-0 overflow-y-auto border-r border-zinc-200 bg-[#f6f7fb] px-4 py-8 xl:w-[340px]">
          <PackageComponentsList
            selectedComponentId={selectedComponentId}
            onSelectComponent={setSelectedComponentId}
            onComponentsChange={onComponentsChange}
            showAddPanel={showAddPanel}
            onShowAddPanel={setShowAddPanel}
            packageMode={packageMode}
          />
        </div>

        {/* Preview - Centered (Mode-dependent content) */}
        <div className="min-w-0 flex-1 overflow-y-auto bg-[#f6f7fb]">
          {isViewMode ? (
            // View mode: Show full rendered package
            <div className="h-full px-5 py-6">
              {/* Generating State */}
              {isGenerating && (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-12 w-12 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 mx-auto mb-4" />
                    <Text className="text-gray-600 font-medium">
                      Generating package...
                    </Text>
                    <Text className="text-sm text-gray-400 mt-2">
                      This may take a few moments
                    </Text>
                  </div>
                </div>
              )}

              {/* Package Preview */}
              {!isGenerating && urlToRender && (
                <MemoizedIframePreview
                  url={urlToRender}
                  title="HIPAA Package"
                />
              )}

              {/* No Preview Available */}
              {!isGenerating && !urlToRender && (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <Text className="text-gray-500">Preview not available</Text>
                    <Text className="text-sm text-gray-400 mt-2">
                      Use the download button to view the package
                    </Text>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Edit mode: Show component-level preview
            <PackagePreviewPane
              components={components}
              selectedComponentId={selectedComponentId}
              matterId={matterId}
              onSelectComponent={setSelectedComponentId}
            />
          )}
        </div>
      </div>
    </div>
  );
};
